/**
 * "Message these people" (backlog #51): from a shift, a selection, a day column, the filtered schedule or the
 * attendance board. The API works out who's on the shifts (published only unless drafts are asked for, no open
 * shifts, nobody on leave, only people in your buildings or teams); you can take people off before sending.
 */
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Info, Megaphone, MessagesSquare, Hash, X, Undo2 } from 'lucide-react';
import {
  SHIFT_MESSAGE_ATTACHMENTS_MAX, SHIFT_MESSAGE_GROUP_DM_MAX, SHIFT_MESSAGE_MODE_LABELS, type ShiftAudienceInput, type ShiftMessageMode,
} from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { discardUpload } from '@/lib/upload';
import { Avatar, Button, ErrorBox, Field, Input, Label, Modal, Select, Spinner, Toggle } from '@/components/ui';
import { EmojiButton, insertAtCaret, restoreCaret } from '@/components/emoji/EmojiButton';
import { AttachButton, UploadChips, useComposerUploads } from '@/components/uploads/ComposerUploads';

export interface MessageShiftsRequest extends Omit<ShiftAudienceInput, 'include_drafts' | 'conversation_id'> {
  /** One shift: offer "this shift only" (same position) or "everyone at that time" (any position). */
  slot?: { onlyLabel: string; everyoneLabel: string };
  /** Starting title for an announcement. */
  defaultTitle: string;
  /** #52: a message the assistant drafted (still reviewed and sent here). */
  initialBody?: string;
  initialMode?: ShiftMessageMode;
}

const MODE_ICON: Record<ShiftMessageMode, typeof Megaphone> = { group_dm: MessagesSquare, channel: Hash, announcement: Megaphone };
const MODE_HINT: Record<ShiftMessageMode, string> = {
  group_dm: 'A new group chat with these people (or the existing one with exactly them).',
  channel: 'Post in a channel you’re in, @mentioning each person.',
  announcement: 'An announcement only these people see, in Announcements and on Home.',
};
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/**
 * `returnTo`: opened from another dialog or panel (Edit shift, the assistant). Cancel / Esc / close go straight back to
 * it with nothing lost, and after sending it offers "Back to shift" (or the given label) next to opening the conversation.
 */
export function MessageShiftsModal({ request, onClose, returnTo }: { request: MessageShiftsRequest; onClose: () => void; returnTo?: { label: string } }) {
  const ws = useWorkspaceId();
  const nav = useNavigate();
  const [sent, setSent] = useState<{ link: string; recipients: number } | null>(null);
  const toast = useUi(s => s.toast);
  const [anyPosition, setAnyPosition] = useState(false);
  const [includeDrafts, setIncludeDrafts] = useState(false);
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<ShiftMessageMode>(request.initialMode ?? 'group_dm');
  const [channel, setChannel] = useState('');
  const [title, setTitle] = useState(request.defaultTitle);
  const [requireAck, setRequireAck] = useState(false);
  const [body, setBody] = useState(request.initialBody ?? '');
  // t509u: off by default; the preview only shows once it's switched on.
  const [details, setDetails] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  // t509u: the chat composer's uploads (same storage, limits, types and chips). They go up as 'shift_message' files
  // (no conversation yet) and the API files them under the group chat, channel or announcement when it's sent.
  const files = useComposerUploads(() => (ws ? { workspace_id: ws, purpose: 'shift_message' } : null), {
    onRemove: u => { if (u.attachment) discardUpload(u.attachment.id); },
  });
  const tooManyFiles = files.uploads.length > SHIFT_MESSAGE_ATTACHMENTS_MAX;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const input: ShiftAudienceInput = useMemo(() => {
    const { slot, defaultTitle: _t, initialBody: _b, initialMode: _m, ...rest } = request;
    void _t; void _b; void _m;
    return {
      ...rest,
      ...(slot && rest.match ? { match: { ...rest.match, any_position: anyPosition }, label: anyPosition ? slot.everyoneLabel : slot.onlyLabel } : {}),
      include_drafts: includeDrafts,
      conversation_id: mode === 'channel' && channel ? channel : undefined,
    };
  }, [request, anyPosition, includeDrafts, mode, channel]);

  const q = useQuery({ queryKey: ['shift-message-audience', ws, input], queryFn: () => api.wf.shiftMessageAudience(ws!, input), enabled: Boolean(ws), placeholderData: p => p });
  const channels = useQuery({ queryKey: ['shift-message-channels', ws], queryFn: () => api.conversations(ws!), enabled: Boolean(ws) && mode === 'channel', staleTime: 60_000 });
  const a = q.data;
  const people = (a?.recipients ?? []).filter(r => !removed.has(r.user_id));
  const gone = (a?.recipients ?? []).filter(r => removed.has(r.user_id));
  const tooManyForGroup = people.length > SHIFT_MESSAGE_GROUP_DM_MAX;
  const outside = new Set((a?.not_in_channel ?? []).map(n => n.user_id));
  const reachable = mode === 'channel' ? people.filter(p => !outside.has(p.user_id)) : people;
  const hasContent = body.trim().length > 0 || files.ready.length > 0;
  const ready = reachable.length > 0 && hasContent && !files.busy && !tooManyFiles && !(mode === 'group_dm' && tooManyForGroup) && !(mode === 'channel' && !channel) && !(mode === 'announcement' && !title.trim());

  const send = async () => {
    setBusy(true); setError(null);
    try {
      const r = await api.wf.sendShiftMessage(ws!, {
        ...input, user_ids: people.map(p => p.user_id), mode, body: body.trim(), include_shift_details: details,
        attachment_ids: files.ready.length ? files.ready.map(f => f.id) : undefined,
        conversation_id: mode === 'channel' ? channel : undefined, title: mode === 'announcement' ? title.trim() : undefined, require_ack: mode === 'announcement' ? requireAck : undefined,
      });
      toast(`Sent to ${plural(r.recipients, 'person', 'people')}${r.not_in_channel.length ? ` · ${plural(r.not_in_channel.length, 'person', 'people')} not in the channel` : ''}`, 'success');
      void queryClient.invalidateQueries({ queryKey: ['wf', ws] });
      void queryClient.invalidateQueries({ queryKey: ['shift-message-notes', ws] });
      if (returnTo) setSent({ link: r.link, recipients: r.recipients });
      else { onClose(); nav(r.link); }
    } catch (e) { setError(e); toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };

  const ex = a?.excluded;
  const notes: string[] = [];
  if (ex?.open) notes.push(`${plural(ex.open, 'open shift')} left out: nobody’s on ${ex.open === 1 ? 'it' : 'them'} yet.`);
  if (ex?.on_leave.length) notes.push(`On leave that day, so left out: ${ex.on_leave.map(p => p.display_name).join(', ')}.`);
  if (ex?.drafts) notes.push(`${plural(ex.drafts, 'person', 'people')} only on draft shifts left out${a?.can_include_drafts ? ' (turn on “Include draft shifts” to add them)' : ''}.`);
  if (ex?.out_of_scope) notes.push(`${plural(ex.out_of_scope, 'shift')} outside your buildings or teams left out.`);
  if (ex?.self) notes.push('You’re on one of these shifts, so you’re not on the list.');

  return (
    sent && returnTo ? (
      <Modal open onClose={onClose} title="Message sent"
        footer={<>
          <Button variant="ghost" onClick={() => { onClose(); nav(sent.link); }} data-testid="shift-message-open">Open the conversation</Button>
          <Button onClick={onClose} data-testid="shift-message-back">{returnTo.label}</Button>
        </>}>
        <p className="text-sm" role="status">Sent to {plural(sent.recipients, 'person', 'people')}.</p>
      </Modal>
    ) :
    <Modal open onClose={onClose} title="Message people on shifts" wide
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button loading={busy} disabled={!ready} onClick={() => void send()} data-testid="shift-message-send">
          Send to {plural(reachable.length, 'person', 'people')}
        </Button>
      </>}>
      <div className="space-y-4" data-testid="shift-message-modal">
        <p className="text-sm font-medium" data-testid="shift-message-label">{input.label}</p>
        {request.slot && (
          <div className="flex flex-col gap-1.5 text-sm sm:flex-row sm:gap-4" role="radiogroup" aria-label="Who to message">
            <label className="flex items-center gap-2"><input type="radio" checked={!anyPosition} onChange={() => setAnyPosition(false)} />{request.slot.onlyLabel}</label>
            <label className="flex items-center gap-2"><input type="radio" checked={anyPosition} onChange={() => setAnyPosition(true)} />{request.slot.everyoneLabel}</label>
          </div>
        )}

        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-sm font-semibold" data-testid="shift-message-count">To: {plural(people.length, 'person', 'people')}</span>
            {q.isFetching && <Spinner className="h-4 w-4" />}
          </div>
          {q.isLoading ? <Spinner className="mx-auto" /> : q.isError ? <ErrorBox error={q.error} /> : !people.length ? (
            <p className="rounded-lg border border-dashed border-slate-300 px-3 py-4 text-center text-sm text-slate-500 dark:border-slate-700">Nobody to message on these shifts.</p>
          ) : (
            <ul className="scroll-thin grid max-h-56 gap-1 overflow-y-auto rounded-lg border border-slate-200 p-1.5 dark:border-slate-700 sm:grid-cols-2" data-testid="shift-message-recipients">
              {people.map(p => (
                <li key={p.user_id} className="flex items-center gap-2 rounded-md px-1.5 py-1 hover:bg-slate-50 dark:hover:bg-white/5">
                  <Avatar size={28} name={p.display_name} id={p.user_id} url={p.avatar_url} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{p.display_name}</span>
                    <span className="block truncate text-xs text-slate-500">{p.shift_label}{p.draft_only ? ' · draft' : ''}{mode === 'channel' && outside.has(p.user_id) ? ' · not in the channel' : ''}</span>
                  </span>
                  <button type="button" className="rounded p-1 text-slate-500 hover:bg-slate-200 hover:text-slate-900 dark:hover:bg-slate-700 dark:hover:text-white"
                    aria-label={`Remove ${p.display_name}`} onClick={() => setRemoved(s => new Set(s).add(p.user_id))}><X className="h-4 w-4" /></button>
                </li>
              ))}
            </ul>
          )}
          {gone.length > 0 && (
            <p className="mt-1.5 flex flex-wrap items-center gap-1 text-xs text-slate-500" data-testid="shift-message-removed">
              Removed: {gone.map(p => p.display_name).join(', ')}
              <button type="button" className="link inline-flex items-center gap-0.5" onClick={() => setRemoved(new Set())}><Undo2 className="h-3 w-3" />Add back</button>
            </p>
          )}
          {notes.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs text-slate-600 dark:text-slate-300" data-testid="shift-message-notes">
              {notes.map(n => <li key={n} className="flex items-start gap-1.5"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />{n}</li>)}
            </ul>
          )}
          {a?.can_include_drafts && <div className="mt-2"><Toggle checked={includeDrafts} onChange={setIncludeDrafts} label="Include draft shifts" /></div>}
        </div>

        <div>
          <span className="mb-1.5 block text-sm font-semibold">Send as</span>
          <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Send as">
            {(['group_dm', 'channel', 'announcement'] as const).map(m => {
              const Icon = MODE_ICON[m];
              const off = m === 'group_dm' && tooManyForGroup;
              return (
                <button key={m} type="button" role="radio" aria-checked={mode === m} disabled={off} onClick={() => setMode(m)} data-testid={`shift-message-mode-${m}`}
                  className={clsx('rounded-lg border px-3 py-2 text-left text-sm transition disabled:cursor-not-allowed disabled:opacity-50',
                    // t509u: like the app's other selection cards: a neutral fill and an accent border, never a yellow block.
                    mode === m ? 'border-accent-line bg-slate-50 ring-1 ring-inset ring-accent-line dark:bg-white/5' : 'border-slate-200 bg-white hover:border-slate-400 dark:border-slate-700 dark:bg-transparent')}>
                  <span className="flex items-center gap-1.5 font-semibold"><Icon className="h-4 w-4" />{SHIFT_MESSAGE_MODE_LABELS[m]}</span>
                  <span className={clsx('mt-0.5 block text-xs', mode === m ? 'text-slate-600 dark:text-slate-300' : 'text-slate-500')}>
                    {off ? `Up to ${SHIFT_MESSAGE_GROUP_DM_MAX} people. Use a channel or an announcement.` : MODE_HINT[m]}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {mode === 'channel' && (
          <Field label="Channel" hint={a?.not_in_channel?.length ? `${plural(a.not_in_channel.length, 'person isn’t', 'people aren’t')} in this channel, so they won’t be mentioned or see it: ${a.not_in_channel.map(n => n.display_name).join(', ')}.` : undefined}>
            <Select value={channel} onChange={e => setChannel(e.target.value)} data-testid="shift-message-channel">
              <option value="">Pick a channel…</option>
              {(channels.data?.conversations ?? []).filter(c => c.kind === 'channel' && c.is_member && !c.archived_at).map(c => <option key={c.id} value={c.id}>#{c.slug ?? c.title}</option>)}
            </Select>
          </Field>
        )}
        {mode === 'announcement' && (
          <div className="space-y-2">
            <Field label="Title"><Input value={title} maxLength={200} onChange={e => setTitle(e.target.value)} data-testid="shift-message-title" /></Field>
            <Toggle checked={requireAck} onChange={setRequireAck} label="Ask them to acknowledge it" />
          </div>
        )}
        <div>
          <Label htmlFor="shift-message-body">Message</Label>
          {/* t509u: one field (same ring and focus as the other fields here) with the text, any files, and a small toolbar (emoji, attach). */}
          <div className="rounded-md bg-white p-0.5 shadow-sm ring-1 ring-inset ring-slate-300 focus-field dark:bg-slate-900 dark:ring-slate-700" data-testid="shift-message-box">
            <textarea id="shift-message-body" ref={bodyRef} rows={4} maxLength={6000} value={body} onChange={e => setBody(e.target.value)} onPaste={files.onPaste}
              placeholder="e.g. The delivery is early tomorrow. Please be in by 08:45." data-testid="shift-message-body"
              className="block w-full resize-none rounded-t-md border-0 bg-transparent px-3 py-2 text-sm outline-none placeholder:text-slate-400 text-slate-900 shadow-none focus:shadow-none focus:ring-0 focus-visible:outline-none dark:text-slate-100" />
            <UploadChips uploads={files.uploads} onRemove={files.remove} className="px-2 pb-1.5" />
            <div className="flex items-center gap-0.5 border-t border-slate-100 px-1.5 py-1 dark:border-slate-800" role="toolbar" aria-label="Message tools" data-testid="shift-message-toolbar">
              <EmojiButton placement="top" onPick={e => { const r = insertAtCaret(bodyRef.current, body, e); setBody(r.value); restoreCaret(bodyRef.current, r.caret); }} />
              <AttachButton onFiles={files.addFiles} />
            </div>
          </div>
          {tooManyFiles && <p className="mt-1 text-xs text-rose-700 dark:text-rose-300" role="alert">Up to {SHIFT_MESSAGE_ATTACHMENTS_MAX} files. Remove some to send.</p>}
        </div>
        <div>
          <Toggle checked={details} onChange={setDetails} label="Add the shift details" />
          {details && a?.details && <p className="mt-1.5 whitespace-pre-line rounded-md bg-slate-50 px-2.5 py-1.5 text-xs text-slate-600 dark:bg-white/5 dark:text-slate-300" data-testid="shift-message-details">{a.details}</p>}
        </div>
        {error ? <ErrorBox error={error} /> : null}
      </div>
    </Modal>
  );
}
